import React, { useState, useEffect, useRef } from 'react'

import { Button, ButtonGroup } from 'components/ui/button'
import { FooterEditEntity } from 'components/ui/footer'
import { Description, Image, Title, Subtitle } from 'components/ui/list-item-label'
import { PopOverMenu, PopOverMenuButton } from 'components/ui/pop-over-menu'
import { PrimaryNavigationEditEntity } from 'components/ui/primary-navigation/edit-entity'
import { SecondaryNavigation } from 'components/ui/secondary-navigation'
import { StatusGuard } from 'components/ui/status-guard'

import { NoRelatedStops, StyledPopOverMenu, RelatedStopListItem } from './related-stops-list-item'
import { SelectRelatedStops } from './select-related-stops'
import { getStopSubnav } from '../subnav'
import { StopDelete } from '../stop-delete'

import { sharedActionCreators } from 'containers/shared'
import { connect } from 'containers/store'

import { actionCreators, GettyActionCreators } from 'domains/org/getty/actions'
import { GettyStore } from 'domains/org/getty/store'
import { stopSchema } from 'domains/org/getty/schemas/stop'
import { releaseControlsBindableActionCreators } from 'domains/release-controls/actions'
import { ReleaseControlUpdate } from 'domains/release-controls/components/release-control-update'

import { AttributeRef } from 'generated/mos/entity'
import { Stop } from 'generated/mos/org/getty/audio'
import { GetStopRequest } from 'generated/mos/org/getty/audioaccess'
import { UpdateStopRequest } from 'generated/mos/org/getty/audiomanagement'
import { Media } from 'generated/mos/media';
import { SetReleaseStatusRequest } from 'generated/mos/releasecontrolsmanagement'

import { bffEndpoint } from 'helpers/bff'
import { areObjectsEqual, pick, Invariant } from 'helpers/core'
import { getResolvedValue } from 'helpers/linkables'
import { Status, statusSelector } from 'helpers/status'

import { LayoutListOrFormPage } from 'layouts/list-or-form-page'
import { StopUpdateTours } from '../stop-update-tours'

type ActionProps =
  Pick<GettyActionCreators, 'gettyGetStopRequest' | 'gettyUpdateStopRequest' | 'gettyUpdateStopReset'> &
  Pick<typeof releaseControlsBindableActionCreators, "releaseControlsListReleaseControlsRequest" | "releaseControlsSetReleaseStatusRequest"> &
  Pick<typeof sharedActionCreators, 'toastNotification'>;

type ConnectedProps =
  Pick<GettyStore['getty'], 'stopUpdate' | 'stops'>

type DirectProps = {
  readonly historyPush: (id: string) => void;
  readonly projectId: string;
  readonly stopId: string;
  readonly tourId?: string;
};
type Props = ActionProps & ConnectedProps & DirectProps

export const RelatedStopsView = (props: Props) => {
  const {
    gettyGetStopRequest,
    gettyUpdateStopRequest,
    gettyUpdateStopReset,
    historyPush,
    projectId,
    stopId,
    stopUpdate,
    toastNotification,
    tourId,
    releaseControlsListReleaseControlsRequest,
    releaseControlsSetReleaseStatusRequest,
  } = props

  const [ addToToursStop, setAddToToursStop ] = useState<Stop.Entity>();
  const [ deleteStopModalOpen, setDeleteStopModalOpen ] = useState(false)
  const [ relatedStopModalOpen, setRelatedStopModalOpen ] = useState(false)
  const [ updatedRelatedStops, setUpdatedRelatedStops ] = useState<ReadonlyArray<Stop.Entity>>()

  const hasSubmitted = useRef(false)

  // where to return to if the user wants to navigate back.
  // if we came here from within a tour, go back to that tour, otherwise go to all stops.
  const backLocation = tourId ? `/project/${projectId}/tour/${tourId}` : `/project/${projectId}/stops`
  const cancelLocation = tourId ? `/project/${projectId}/tour/${tourId}/stop/${stopId}` : `/project/${projectId}/stop/${stopId}`

  const stopData = statusSelector.data(stopUpdate)
  const stop = stopData ? stopData.stop : undefined
  const tours = stopData ? stopData.tours : undefined
  const stopTitle = stopData && stopData.stop.title ? getResolvedValue(stopSchema.title.read(stopData.stop.title), stopData.resolvedValues || []) : ''

  const relatedStops: ReadonlyArray<Stop.Entity> = stopData && stopData.relatedStops ? stopData.relatedStops : []

  const modified = stopData && updatedRelatedStops && !areObjectsEqual(updatedRelatedStops, relatedStops)

  const hostName = bffEndpoint(window.location.hostname)

  useEffect(() =>{
    if (stopUpdate.status === Status.Idle || (stop && Stop.mustRef(stop.ref).id !== stopId)) {
      gettyGetStopRequest({
        type: GetStopRequest.refName,
        stopRef: {
          typename: Stop.refName,
          id: stopId,
        }
      })
      releaseControlsListReleaseControlsRequest([{
        typename: Stop.refName,
        id: stopId,
      }])
      Stop.refName
    }

    if (stopUpdate.status === Status.Ready) {
      setUpdatedRelatedStops(relatedStops)
    }

    if(stopUpdate.status === Status.Ready && hasSubmitted.current) {
      releaseControlsListReleaseControlsRequest([{
        typename: Stop.refName,
        id: stopId,
      }])
      toastNotification({ type: 'success', text: 'Stop updated' })
      hasSubmitted.current = false
    }

    // if the status is stale, trigger failure toast
    if (stopUpdate.status === Status.Stale) {
      gettyUpdateStopReset()
      toastNotification({ type: 'error', text: 'Can’t save because of an unknown error' })
      hasSubmitted.current = false
    }
  }, [stopUpdate.status]) // eslint-disable-line react-hooks/exhaustive-deps

  const onSave = () => {
    const relatedStopRefs = updatedRelatedStops && updatedRelatedStops.map((stop) => Stop.mustRef(stop.ref))

    if (stopUpdate.status === Status.Ready && relatedStopRefs) {
      gettyUpdateStopRequest({
        ...UpdateStopRequest.defaults,
        stopRef: stop && stop.ref,
        updateMask: {
          type: 'google.protobuf.FieldMask',
          fields: [
            'relatedStopRefs'
          ]
        },
        relatedStopRefs: relatedStopRefs
      })
      hasSubmitted.current = true
    }
  }

  const onSelectingStops = (selectedStops: ReadonlyArray<Stop.Entity>) => {
    setUpdatedRelatedStops(updatedRelatedStops && [...updatedRelatedStops, ...selectedStops])
  }

  const onRemovingStop = (stopToRemove: Stop.Entity) => {
    const filteredRelatedStops = updatedRelatedStops && updatedRelatedStops.filter((stop) => !areObjectsEqual(stop, stopToRemove))
    setUpdatedRelatedStops(filteredRelatedStops)
  }

  const releaseControlUpdate = (bundles: ReadonlyArray<SetReleaseStatusRequest.BundleStatus.Entity>) => {
    stop && releaseControlsSetReleaseStatusRequest({
      ...SetReleaseStatusRequest.defaults,
      ref: stop.ref,
      bundles,
    })
  }

  return (
    <>
      {addToToursStop && (
        <StopUpdateTours
          stop={addToToursStop}
          onCancel={() => setAddToToursStop(undefined)}
          onComplete={() => setAddToToursStop(undefined)}
        />
      )}
      {deleteStopModalOpen && (
        <StopDelete
          historyPush={historyPush}
          projectId={projectId}
          onCancel={() => setDeleteStopModalOpen(false)}
          stop={stop}
          tours={tours}
        />
      )}
      {relatedStopModalOpen &&
        stop &&
        relatedStops &&
        updatedRelatedStops && (
        <SelectRelatedStops
          currentRelatedStops = {updatedRelatedStops.map((stop) => Stop.mustRef(stop.ref))}
          onCancel={() => setRelatedStopModalOpen(false)}
          onComplete={(selectedStops) => onSelectingStops(selectedStops)}
        />
      )}
      <LayoutListOrFormPage
        nav={() => (
          <PrimaryNavigationEditEntity
            title="Edit Stop"
            onConfirm={modified ? () => historyPush(backLocation) : undefined}
            navBackRoute={backLocation}
          />
        )}
        title={stopTitle as string}
        toolbar={() => (
          <ButtonGroup>
            {stop &&
              <ReleaseControlUpdate
                hasBorder
                entityRef={stop.ref}
                updateHandler={releaseControlUpdate}
              />
            }
            <PopOverMenu hasBorder>
              <PopOverMenuButton onClick={() => setAddToToursStop(stop)}>
                Add to Tour
              </PopOverMenuButton>
              <PopOverMenuButton onClick={() => setDeleteStopModalOpen(true)}>
                Delete
              </PopOverMenuButton>
            </PopOverMenu>
          </ButtonGroup>
        )}
        subnav={() => (
          <SecondaryNavigation navItems={getStopSubnav(projectId, stopId, tourId)} modified={modified} historyPush={historyPush} >
            <Button variant="link" appearance="secondary" onClick={() => setRelatedStopModalOpen(true)}>Add stops</Button>
          </SecondaryNavigation>
        )}
        content={() => (
          <StatusGuard status={stopUpdate}>
            { updatedRelatedStops && !updatedRelatedStops.length || !updatedRelatedStops ? (
              <NoRelatedStops>
                No related stops
              </NoRelatedStops>
            ) :
            (updatedRelatedStops && updatedRelatedStops.map(relatedStop => {
              const title: any = stopSchema.title.read(relatedStop.title)
              const producedBy: any = stopSchema.producedBy.read(relatedStop.producedBy)

              const stopImage = stopSchema.image.read(relatedStop.image) as Media.Ref
              const stopImageId = stopImage && stopImage.id
              const imagePath = stopImageId && hostName ? `${hostName}/images/${stopImageId}.jpg?s=crop&h=92&w=92` : ''

              // check if attribute refs are still present and throw invariant if they are
              if (title && title.type === AttributeRef.refName || producedBy && producedBy.type === AttributeRef.refName) {
                throw new Invariant('Attribute ref did not resolve to a string value')
              }

              return (
                <RelatedStopListItem key={Stop.mustRef(relatedStop.ref).id}>
                  <Image src={imagePath} />
                  <Description>
                    <Title>{title}</Title>
                    <Subtitle>{producedBy ? producedBy : '-'}</Subtitle>
                  </Description>
                  <StyledPopOverMenu>
                    <PopOverMenu iconSize="base">
                      <PopOverMenuButton onClick={() => {
                        onRemovingStop(relatedStop)
                      }}>
                        Remove
                      </PopOverMenuButton>
                    </PopOverMenu>
                  </StyledPopOverMenu>
                </RelatedStopListItem>
              )
            })
            )}
          </StatusGuard>
        )}
        sidebar={() => null}
        footer={() => stop && (
          <FooterEditEntity
            entity={stopUpdate}
            modified={modified}
            onCancel={() => historyPush(cancelLocation)}
            onSave={() => {
              onSave()
              return false
            }}
          />
        )}
      />
    </>
  )
}

export const RelatedStops = connect<ConnectedProps, ActionProps, DirectProps, GettyStore, GettyActionCreators>(
  store => ({
    ...pick(store.getty, 'stopUpdate', 'stops'),
  }),
  {
    ...pick(actionCreators, 'gettyGetStopRequest', 'gettyUpdateStopRequest', 'gettyUpdateStopReset'),
    ...pick(releaseControlsBindableActionCreators, 'releaseControlsListReleaseControlsRequest', 'releaseControlsSetReleaseStatusRequest'),
    ...pick(sharedActionCreators, 'toastNotification'),
  }
)(RelatedStopsView)
